import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  ArrowLeft,
  Users,
  Eye,
  MessageCircleQuestion,
  KeyRound,
  Loader2,
  Sparkles,
  Search,
  Copy,
  Check,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

// ---------- Reusable stat tile ----------
function Stat({ label, value, sub, icon: Icon, testid }) {
  return (
    <div
      data-testid={testid}
      className="border border-slate-200 bg-white p-6 flex items-start justify-between gap-4 transition-colors hover:border-slate-900"
    >
      <div className="min-w-0">
        <p className="eyebrow">{label}</p>
        <p
          className="font-display mt-2 leading-none tracking-tight"
          style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}
        >
          {value}
        </p>
        {sub ? (
          <p className="mt-2 text-xs text-slate-500 leading-relaxed">{sub}</p>
        ) : null}
      </div>
      <span
        className="shrink-0 w-10 h-10 grid place-items-center"
        style={{ background: "var(--beige)", color: "var(--gold-deep)" }}
      >
        <Icon className="w-4 h-4" />
      </span>
    </div>
  );
}

// ---------- Reset-password modal ----------
function ResetPasswordModal({ target, onClose, onDone, isAr }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const run = async () => {
    if (!target) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/users/${target.id}/reset-password`);
      setResult(r.data);
      toast.success(isAr ? "تم إنشاء كلمة مرور جديدة" : "New password generated");
      onDone?.();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (isAr ? "فشل التنفيذ" : "Failed"));
    } finally {
      setBusy(false);
    }
  };

  const copyPw = async () => {
    if (!result?.new_password) return;
    try {
      await navigator.clipboard.writeText(result.new_password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(isAr ? "تعذّر النسخ" : "Copy failed");
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="reset-password-modal"
        dir={isAr ? "rtl" : "ltr"}
        className="max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {isAr ? "إعادة تعيين كلمة المرور" : "Reset user password"}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-600 leading-relaxed">
            {result
              ? (isAr
                  ? "تم توليد كلمة مرور جديدة. انسخها وسلّمها للعميل الآن — لن تظهر مرة أخرى."
                  : "A new password was generated. Copy it and hand it to the client now — it won't be shown again.")
              : (isAr
                  ? "سيتم توليد كلمة مرور عشوائية آمنة وإرسالها بالبريد للمستخدم (إن كان البريد مُهيّأ)، ويُطلب منه تغييرها عند أول دخول."
                  : "A secure random password will be generated and emailed to the user (when SMTP is configured). They will be forced to change it on first login.")}
          </DialogDescription>
        </DialogHeader>

        {target ? (
          <div
            className="border border-slate-200 bg-slate-50 px-4 py-3 text-sm"
            data-testid="reset-target-summary"
          >
            <p className="font-medium">{target.name || "—"}</p>
            <p className="text-xs text-slate-500 mt-0.5" dir="ltr">
              {target.email}
            </p>
            <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400 mt-1.5">
              {target.role}
            </p>
          </div>
        ) : null}

        {result ? (
          <div
            className="border-s-4 ps-4 py-3 bg-amber-50/60 mt-2"
            style={{ borderColor: "var(--gold-deep)" }}
            data-testid="reset-result-block"
          >
            <p
              className="text-[11px] uppercase tracking-[0.2em] mb-1"
              style={{ color: "var(--gold-deep)" }}
            >
              {isAr ? "كلمة المرور الجديدة" : "New password"}
            </p>
            <div className="flex items-center gap-2">
              <code
                data-testid="reset-new-password"
                dir="ltr"
                className="flex-1 font-mono text-base bg-white border border-slate-200 px-3 py-2 select-all"
              >
                {result.new_password}
              </code>
              <Button
                type="button"
                onClick={copyPw}
                data-testid="reset-copy-btn"
                variant="outline"
                className="rounded-sm h-10"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-2 leading-relaxed">
              {result.email_sent
                ? (isAr
                    ? "✓ تم إرسال نسخة بالبريد للمستخدم."
                    : "✓ A copy was emailed to the user.")
                : (isAr
                    ? "⚠ لم يُرسل البريد. سلّم كلمة المرور يدوياً."
                    : "⚠ Email was not sent. Hand the password to the user manually.")}
            </p>
          </div>
        ) : null}

        <DialogFooter className="mt-2">
          {!result ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={busy}
                data-testid="reset-cancel-btn"
              >
                {isAr ? "إلغاء" : "Cancel"}
              </Button>
              <Button
                type="button"
                onClick={run}
                disabled={busy}
                className="btn-gold rounded-sm"
                data-testid="reset-confirm-btn"
              >
                {busy ? (
                  <Loader2 className="w-4 h-4 animate-spin me-2" />
                ) : (
                  <KeyRound className="w-4 h-4 me-2" />
                )}
                {isAr ? "إنشاء كلمة مرور" : "Generate password"}
              </Button>
            </>
          ) : (
            <Button
              type="button"
              onClick={onClose}
              className="btn-gold rounded-sm"
              data-testid="reset-done-btn"
            >
              {isAr ? "تم" : "Done"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminAnalytics() {
  const { lang } = useI18n();
  const { user, loading } = useAuth();
  const isAr = lang === "ar";

  const [overview, setOverview] = useState(null);
  const [topics, setTopics] = useState(null);
  const [topicsRefreshing, setTopicsRefreshing] = useState(false);
  const [questions, setQuestions] = useState(null);
  const [usersList, setUsersList] = useState(null);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersFilter, setUsersFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [resetTarget, setResetTarget] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  const loadOverview = useCallback(() => {
    api.get("/admin/analytics/overview").then((r) => setOverview(r.data)).catch(() => setOverview({}));
  }, []);

  const loadTopics = useCallback((forceRefresh = false) => {
    if (forceRefresh) setTopicsRefreshing(true);
    api
      .get("/admin/analytics/topics", {
        params: { window_days: 60, refresh: forceRefresh ? "true" : undefined },
        timeout: 60000,
      })
      .then((r) => setTopics(r.data))
      .catch(() => setTopics({ topics: [], sample_size: 0 }))
      .finally(() => setTopicsRefreshing(false));
  }, []);

  const loadQuestions = useCallback(() => {
    api
      .get("/admin/analytics/questions", { params: { limit: 50 } })
      .then((r) => setQuestions(r.data?.items || []))
      .catch(() => setQuestions([]));
  }, []);

  const loadUsers = useCallback(() => {
    const params = { limit: 200 };
    if (usersFilter.trim()) params.q = usersFilter.trim();
    if (roleFilter !== "all") params.role = roleFilter;
    api
      .get("/admin/users/all", { params })
      .then((r) => {
        setUsersList(r.data?.items || []);
        setUsersTotal(r.data?.total ?? 0);
      })
      .catch(() => setUsersList([]));
  }, [usersFilter, roleFilter]);

  useEffect(() => {
    if (user?.role !== "admin") return;
    loadOverview();
    loadQuestions();
    loadTopics();
  }, [user, loadOverview, loadQuestions, loadTopics]);

  useEffect(() => {
    if (user?.role !== "admin") return;
    const handle = setTimeout(loadUsers, 250);
    return () => clearTimeout(handle);
  }, [user, loadUsers]);

  const deleteUser = async (target) => {
    if (target.id === user?.id) return;
    if (!window.confirm(
      isAr
        ? `هل تريد حذف حساب ${target.email}؟ لا يمكن التراجع.`
        : `Delete the account ${target.email}? This cannot be undone.`
    )) return;
    try {
      await api.delete(`/admin/users/${target.id}`);
      toast.success(isAr ? "تم حذف الحساب" : "Account deleted");
      loadUsers();
      loadOverview();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (isAr ? "فشل الحذف" : "Delete failed"));
    }
  };

  const t = useMemo(() => ({
    back: isAr ? "← لوحة التحكم" : "← Dashboard",
    eyebrow: isAr ? "تحليلات ومستخدمون" : "Analytics & users",
    title: isAr ? "نبض الموقع." : "The pulse of the firm.",
    lead: isAr
      ? "أسئلة سامي، زوّار الموقع، الحسابات المسجّلة — وكل ما يلزم لإدارة كلمات المرور والصلاحيات."
      : "Sami questions, site visitors, registered accounts — and the controls to manage passwords and permissions.",
    tabs: {
      overview: isAr ? "نظرة عامة" : "Overview",
      questions: isAr ? "الأسئلة" : "Questions",
      users: isAr ? "الحسابات" : "Users",
    },
    questionsTotal: isAr ? "إجمالي الأسئلة" : "Questions (all-time)",
    last7: isAr ? "آخر 7 أيام" : "Last 7 days",
    last30: isAr ? "آخر 30 يوم" : "Last 30 days",
    visitorsTitle: isAr ? "زوار فريدون" : "Unique visitors",
    visitors24h: isAr ? "آخر 24 ساعة" : "Last 24 hours",
    visitorsWeek: isAr ? "آخر 7 أيام" : "Last 7 days",
    visitorsMonth: isAr ? "آخر 30 يوم" : "Last 30 days",
    pageViews: isAr ? "إجمالي الزيارات (page views)" : "Total page views",
    usersTitle: isAr ? "الحسابات" : "Accounts",
    totalUsers: isAr ? "إجمالي الحسابات" : "Total accounts",
    clients: isAr ? "عملاء" : "Clients",
    staffAdmin: isAr ? "فريق ومسؤولون" : "Staff & admins",
    signupsWeek: isAr ? "تسجيلات (7 أيام)" : "Signups (7d)",
    signupsMonth: isAr ? "تسجيلات (30 يوم)" : "Signups (30d)",
    searchUsers: isAr ? "ابحث بالاسم أو البريد..." : "Search by name or email…",
    role: isAr ? "الدور" : "Role",
    roles: {
      all: isAr ? "كل الأدوار" : "All roles",
      client: isAr ? "عميل" : "Client",
      staff: isAr ? "فريق" : "Staff",
      admin: isAr ? "مسؤول" : "Admin",
    },
    name: isAr ? "الاسم" : "Name",
    email: isAr ? "البريد الإلكتروني" : "Email",
    created: isAr ? "تاريخ التسجيل" : "Created",
    verified: isAr ? "مفعّل" : "Verified",
    actions: isAr ? "إجراءات" : "Actions",
    resetPw: isAr ? "إعادة تعيين كلمة المرور" : "Reset password",
    deleteAcc: isAr ? "حذف" : "Delete",
    noUsers: isAr ? "لا توجد حسابات تطابق البحث." : "No accounts match.",
    noQuestions: isAr ? "لم تُسجَّل أسئلة بعد." : "No questions logged yet.",
    questionRow: isAr ? "السؤال" : "Question",
    askedAt: isAr ? "التاريخ" : "Asked",
    tier: isAr ? "المستوى" : "Tier",
    visitorsHint: isAr
      ? "نتبع جلسة كل زائر دون أي بيانات شخصية — فقط عدّاد للجلسات الفريدة."
      : "We track a per-session id with no PII — only a unique-session counter.",
    selfWarn: isAr
      ? "لا يمكنك إعادة تعيين كلمة المرور لحسابك من هنا. استخدم صفحة الإعدادات."
      : "You cannot reset your own password here. Use the settings page.",
  }), [isAr]);

  if (loading) {
    return <div className="p-20 text-center text-slate-500 text-sm">Loading…</div>;
  }
  if (!user) return <Navigate to="/login?next=/admin/analytics" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div
      data-testid="admin-analytics-page"
      className="max-w-7xl mx-auto px-6 lg:px-10 py-20"
    >
      <Link
        to="/admin"
        data-testid="back-to-dashboard"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-slate-900 transition-colors mb-8"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        {t.back}
      </Link>

      <div className="grid lg:grid-cols-12 gap-12 mb-12">
        <div className="lg:col-span-6">
          <p className="eyebrow">{t.eyebrow}</p>
          <h1 className="font-display text-5xl lg:text-6xl mt-4 leading-[1.0] tracking-tight">
            {t.title}
          </h1>
        </div>
        <div className="lg:col-span-6 lg:pt-3">
          <p className="text-lg text-slate-600 leading-relaxed">{t.lead}</p>
        </div>
      </div>

      {/* Tabs */}
      <div
        role="tablist"
        className="inline-flex border border-slate-200 bg-white rounded-sm overflow-hidden mb-10"
      >
        {[
          ["overview", t.tabs.overview],
          ["questions", t.tabs.questions],
          ["users", t.tabs.users],
        ].map(([id, label]) => (
          <button
            key={id}
            type="button"
            data-testid={`analytics-tab-${id}`}
            onClick={() => setActiveTab(id)}
            className={`px-5 h-11 text-xs uppercase tracking-[0.18em] transition-colors border-s first:border-s-0 border-slate-200 ${
              activeTab === id
                ? "bg-slate-900 text-white"
                : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* OVERVIEW */}
      {activeTab === "overview" && (
        <div className="space-y-10">
          {/* Visitors */}
          <section>
            <h2 className="font-display text-2xl tracking-tight mb-4">
              {t.visitorsTitle}
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <Stat
                testid="stat-visitors-24h"
                label={t.visitors24h}
                value={overview ? (overview.visitors?.unique_24h ?? 0).toLocaleString(isAr ? "ar" : "en") : "—"}
                icon={Eye}
              />
              <Stat
                testid="stat-visitors-7d"
                label={t.visitorsWeek}
                value={overview ? (overview.visitors?.unique_7d ?? 0).toLocaleString(isAr ? "ar" : "en") : "—"}
                icon={Eye}
              />
              <Stat
                testid="stat-visitors-30d"
                label={t.visitorsMonth}
                value={overview ? (overview.visitors?.unique_30d ?? 0).toLocaleString(isAr ? "ar" : "en") : "—"}
                icon={Eye}
              />
              <Stat
                testid="stat-page-views"
                label={t.pageViews}
                value={overview ? (overview.visitors?.page_views_total ?? 0).toLocaleString(isAr ? "ar" : "en") : "—"}
                icon={Eye}
              />
            </div>
            <p className="mt-3 text-xs text-slate-500 leading-relaxed">
              {t.visitorsHint}
            </p>
          </section>

          {/* AI-clustered trending topics (last 60 days) */}
          <section data-testid="trending-topics-section">
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
              <div>
                <h2 className="font-display text-2xl tracking-tight inline-flex items-center gap-2">
                  <Sparkles className="w-4 h-4" style={{ color: "var(--gold-deep)" }} />
                  {isAr ? "أكثر المواضيع رواجاً" : "Trending topics"}
                </h2>
                <p className="text-xs text-slate-500 mt-1">
                  {isAr
                    ? "تجميع تلقائي بالذكاء الاصطناعي لأسئلة سامي في آخر 60 يوماً."
                    : "AI-clustered from the last 60 days of Sami questions."}
                </p>
              </div>
              <Button
                type="button"
                onClick={() => loadTopics(true)}
                disabled={topicsRefreshing}
                variant="outline"
                size="sm"
                data-testid="refresh-topics-btn"
                className="rounded-sm h-9"
              >
                {topicsRefreshing ? (
                  <Loader2 className="w-3.5 h-3.5 me-1.5 animate-spin" />
                ) : (
                  <Sparkles className="w-3.5 h-3.5 me-1.5" />
                )}
                {isAr ? "تحديث" : "Refresh"}
              </Button>
            </div>

            {topics === null ? (
              <div className="p-10 text-center text-slate-500 text-sm border border-slate-200">
                <Loader2 className="w-4 h-4 animate-spin mx-auto" />
              </div>
            ) : topics.topics && topics.topics.length > 0 ? (
              <ul className="border border-slate-200 bg-white divide-y divide-slate-100" data-testid="topics-list">
                {topics.topics.map((tp, i) => (
                  <li
                    key={i}
                    data-testid={`topic-row-${i}`}
                    className="px-5 py-4 flex items-center gap-4"
                  >
                    <span
                      className="shrink-0 font-display text-2xl leading-none w-8 text-end"
                      style={{ color: "var(--gold-deep)" }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-3 mb-1.5">
                        <p className="font-display text-base truncate" dir="rtl">
                          {tp.title}
                        </p>
                        <p className="shrink-0 text-sm tabular-nums" style={{ color: "var(--gold-deep)" }}>
                          {tp.share.toFixed(1)}%
                          <span className="text-[10px] text-slate-400 ms-1.5">
                            ({tp.count})
                          </span>
                        </p>
                      </div>
                      {/* Bar */}
                      <div className="h-1.5 bg-slate-100 rounded-sm overflow-hidden">
                        <div
                          className="h-full transition-all"
                          style={{
                            width: `${Math.min(100, tp.share)}%`,
                            background: "var(--gold-deep)",
                          }}
                        />
                      </div>
                      {tp.sample ? (
                        <p
                          className="mt-2 text-xs text-slate-500 line-clamp-1 italic"
                          dir="rtl"
                        >
                          «{tp.sample}»
                        </p>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500">
                {isAr
                  ? "لا توجد أسئلة كافية للتجميع بعد."
                  : "Not enough questions yet to cluster."}
              </div>
            )}
          </section>

          {/* Questions */}
          <section>
            <h2 className="font-display text-2xl tracking-tight mb-4">
              {t.tabs.questions}
            </h2>
            <div className="grid sm:grid-cols-3 gap-3">
              <Stat
                testid="stat-questions-total"
                label={t.questionsTotal}
                value={overview ? (overview.questions?.total ?? 0).toLocaleString(isAr ? "ar" : "en") : "—"}
                icon={MessageCircleQuestion}
              />
              <Stat
                testid="stat-questions-7d"
                label={t.last7}
                value={overview ? (overview.questions?.last_7_days ?? 0).toLocaleString(isAr ? "ar" : "en") : "—"}
                icon={MessageCircleQuestion}
              />
              <Stat
                testid="stat-questions-30d"
                label={t.last30}
                value={overview ? (overview.questions?.last_30_days ?? 0).toLocaleString(isAr ? "ar" : "en") : "—"}
                icon={MessageCircleQuestion}
              />
            </div>
          </section>

          {/* Users */}
          <section>
            <h2 className="font-display text-2xl tracking-tight mb-4">
              {t.usersTitle}
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <Stat
                testid="stat-users-total"
                label={t.totalUsers}
                value={overview ? (overview.users?.total ?? 0).toLocaleString(isAr ? "ar" : "en") : "—"}
                icon={Users}
              />
              <Stat
                testid="stat-users-clients"
                label={t.clients}
                value={overview ? (overview.users?.clients ?? 0).toLocaleString(isAr ? "ar" : "en") : "—"}
                icon={Users}
              />
              <Stat
                testid="stat-users-staff"
                label={t.staffAdmin}
                value={overview ? ((overview.users?.staff ?? 0) + (overview.users?.admins ?? 0)).toLocaleString(isAr ? "ar" : "en") : "—"}
                icon={ShieldCheck}
              />
              <Stat
                testid="stat-users-signups-7d"
                label={t.signupsWeek}
                value={overview ? (overview.users?.signups_last_7_days ?? 0).toLocaleString(isAr ? "ar" : "en") : "—"}
                icon={Users}
              />
              <Stat
                testid="stat-users-signups-30d"
                label={t.signupsMonth}
                value={overview ? (overview.users?.signups_last_30_days ?? 0).toLocaleString(isAr ? "ar" : "en") : "—"}
                icon={Users}
              />
            </div>
          </section>
        </div>
      )}

      {/* QUESTIONS */}
      {activeTab === "questions" && (
        <div>
          {questions === null ? (
            <div className="p-16 text-center text-slate-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin mx-auto" />
            </div>
          ) : questions.length === 0 ? (
            <div className="border border-dashed border-slate-300 p-16 text-center text-slate-500">
              {t.noQuestions}
            </div>
          ) : (
            <div className="border border-slate-200 bg-white overflow-x-auto">
              <table className="w-full text-sm" data-testid="questions-table">
                <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-[10px]">
                  <tr>
                    <th className="text-start px-5 py-3 w-[60%]">{t.questionRow}</th>
                    <th className="text-start px-5 py-3">{t.tier}</th>
                    <th className="text-start px-5 py-3">{t.email}</th>
                    <th className="text-start px-5 py-3">{t.askedAt}</th>
                  </tr>
                </thead>
                <tbody>
                  {questions.map((q) => (
                    <tr
                      key={q.id}
                      data-testid={`question-row-${q.id}`}
                      className="border-t border-slate-100 hover:bg-slate-50"
                    >
                      <td className="px-5 py-3 align-top" dir="auto">
                        <p className="line-clamp-2 leading-relaxed">{q.user_message}</p>
                      </td>
                      <td className="px-5 py-3 align-top text-xs uppercase tracking-[0.15em] text-slate-500">
                        {q.tier || "public"}
                      </td>
                      <td className="px-5 py-3 align-top text-xs text-slate-500" dir="ltr">
                        {q.user_email || "—"}
                      </td>
                      <td className="px-5 py-3 align-top text-xs text-slate-500 whitespace-nowrap" dir="ltr">
                        {q.created_at ? new Date(q.created_at).toLocaleString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* USERS */}
      {activeTab === "users" && (
        <div>
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 absolute start-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <Input
                value={usersFilter}
                onChange={(e) => setUsersFilter(e.target.value)}
                placeholder={t.searchUsers}
                data-testid="users-search"
                dir="auto"
                className="rounded-sm h-10 ps-9"
              />
            </div>
            <div className="inline-flex border border-slate-200 bg-white rounded-sm overflow-hidden">
              {["all", "client", "staff", "admin"].map((r) => (
                <button
                  key={r}
                  type="button"
                  data-testid={`users-role-${r}`}
                  onClick={() => setRoleFilter(r)}
                  className={`px-3 h-10 text-xs uppercase tracking-[0.18em] border-s first:border-s-0 border-slate-200 transition-colors ${
                    roleFilter === r ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {t.roles[r]}
                </button>
              ))}
            </div>
            <p className="ms-auto text-xs text-slate-500" data-testid="users-total">
              {usersTotal.toLocaleString(isAr ? "ar" : "en")}{" "}
              {isAr ? "حساب" : "accounts"}
            </p>
          </div>

          {usersList === null ? (
            <div className="p-16 text-center text-slate-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin mx-auto" />
            </div>
          ) : usersList.length === 0 ? (
            <div
              className="border border-dashed border-slate-300 p-16 text-center text-slate-500"
              data-testid="users-empty"
            >
              {t.noUsers}
            </div>
          ) : (
            <div className="border border-slate-200 bg-white overflow-x-auto">
              <table className="w-full text-sm" data-testid="users-table">
                <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-[10px]">
                  <tr>
                    <th className="text-start px-5 py-3">{t.name}</th>
                    <th className="text-start px-5 py-3">{t.email}</th>
                    <th className="text-start px-5 py-3">{t.role}</th>
                    <th className="text-start px-5 py-3">{t.created}</th>
                    <th className="text-end px-5 py-3">{t.actions}</th>
                  </tr>
                </thead>
                <tbody>
                  {usersList.map((u) => {
                    const isSelf = u.id === user?.id;
                    return (
                      <tr
                        key={u.id}
                        data-testid={`user-row-${u.id}`}
                        className="border-t border-slate-100 hover:bg-slate-50"
                      >
                        <td className="px-5 py-3 align-top">
                          {u.name || "—"}
                          {!u.email_verified && (
                            <span className="ms-2 text-[10px] uppercase tracking-[0.15em] text-amber-700">
                              {isAr ? "غير مفعل" : "unverified"}
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-3 align-top text-xs text-slate-600" dir="ltr">
                          {u.email}
                        </td>
                        <td className="px-5 py-3 align-top text-[11px] uppercase tracking-[0.18em] text-slate-500">
                          {u.role}
                        </td>
                        <td className="px-5 py-3 align-top text-xs text-slate-500 whitespace-nowrap" dir="ltr">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                        </td>
                        <td className="px-5 py-3 align-top text-end whitespace-nowrap">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={isSelf}
                            title={isSelf ? t.selfWarn : ""}
                            onClick={() => setResetTarget(u)}
                            data-testid={`reset-pw-${u.id}`}
                            className="rounded-sm h-8 me-2"
                          >
                            <KeyRound className="w-3.5 h-3.5 me-1.5" />
                            {t.resetPw}
                          </Button>
                          {u.role !== "admin" && !isSelf && (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => deleteUser(u)}
                              data-testid={`delete-user-${u.id}`}
                              className="rounded-sm h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <ResetPasswordModal
        target={resetTarget}
        onClose={() => setResetTarget(null)}
        onDone={loadOverview}
        isAr={isAr}
      />
    </div>
  );
}
